import React, { useEffect, useRef, useState } from "react";
import {
  FaUserCircle,
  FaInfoCircle,
  FaMicrochip,
  FaKey,
  FaPalette,
  FaCheck,
  FaSignOutAlt,
  FaUserPlus,
} from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import UserDevicesModal from "./UserDevicesModal";
import UserInfoModal from "./UserInfoModal";
import UserLogoutConfirmModal from "./UserLogoutConfirmModal";
import UserChangePasswordModal from "./UserChangePasswordModal";
import StaffManagementModal from "./StaffManagementModal";
import { request } from "../../services/api";
import styles from "./UserMenu.module.css";
import { useLocalTheme } from "../../theme/useLocalTheme";

const UserMenu = ({
  user = null,
  userName = "",
  onLogout,
  onSaveUserInfo,
  dropdownAlign = "right",

  routes = {
    devices: "/dashboard",
    changePassword: "/change-password",
    userSettings: "/user-settings",
    login: "/login",
  },
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isThemePanelOpen, setIsThemePanelOpen] = useState(false);
  const [isInfoModalOpen, setIsInfoModalOpen] = useState(false);
  const [isDevicesModalOpen, setIsDevicesModalOpen] = useState(false);
  const [isLogoutConfirmOpen, setIsLogoutConfirmOpen] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const [isChangePasswordModalOpen, setIsChangePasswordModalOpen] =
    useState(false);
  const [isStaffManagementModalOpen, setIsStaffManagementModalOpen] =
    useState(false);
  const [canManageStaff, setCanManageStaff] = useState(false);
  const [isCheckingStaffAccess, setIsCheckingStaffAccess] = useState(false);
  const menuRef = useRef(null);
  const isMountedRef = useRef(true);
  const navigate = useNavigate();
  const { themeKey, themes, setTheme } = useLocalTheme();

  const getStoredUser = () => {
    if (user) return user;

    try {
      const storedUser =
        localStorage.getItem("user") || localStorage.getItem("currentUser");

      if (!storedUser) return null;

      return JSON.parse(storedUser);
    } catch (error) {
      console.error("خطا در خواندن اطلاعات کاربر:", error);
      return null;
    }
  };

  const checkStaffManagementAccess = async () => {
    const currentUser = getStoredUser();

    if (!currentUser?.role) {
      setCanManageStaff(false);
      return;
    }

    if (currentUser.role === "customer") {
      setCanManageStaff(true);
      return;
    }

    if (currentUser.role !== "staff") {
      setCanManageStaff(false);
      return;
    }

    try {
      setIsCheckingStaffAccess(true);

      const json = await request("/get_manageable_devices.php");

      if (!isMountedRef.current) return;

      const allowed =
        json?.status === "success" &&
        json?.can_manage_staff === true &&
        Array.isArray(json?.devices) &&
        json.devices.length > 0;

      setCanManageStaff(allowed);
    } catch (error) {
      console.error("خطا در بررسی امکان مدیریت کارمندان:", error);

      /*
        در خطای موقت شبکه، آماده نبودن token یا کندی API،
        مقدار قبلی را false نمی‌کنیم تا گزینه مدیریت کارمندان بی‌دلیل حذف نشود.
        اگر کاربر واقعاً دسترسی نداشته باشد، backend در خود APIهای مدیریت کارمندان
        همچنان درخواست‌ها را رد می‌کند.
      */
    } finally {
      if (isMountedRef.current) {
        setIsCheckingStaffAccess(false);
      }
    }
  };

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    checkStaffManagementAccess();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setIsOpen(false);
        setIsThemePanelOpen(false);
      }
    };

    const handleOpenLogoutConfirm = () => {
      closeMenu();
      setIsLogoutConfirmOpen(true);
    };

    const handleConfirmLogout = async () => {
      try {
        setIsLoggingOut(true);

        if (onLogout) {
          const result = onLogout();

          if (result && typeof result.then === "function") {
            await result;
          }

          setIsLogoutConfirmOpen(false);
          return;
        }

        localStorage.removeItem("token");
        localStorage.removeItem("user");
        localStorage.removeItem("currentUser");
        localStorage.removeItem("device");
        localStorage.removeItem("selectedDevice");

        setIsLogoutConfirmOpen(false);
        navigate(routes.login || "/login");
      } catch (error) {
        console.error("خطا در خروج از حساب:", error);
      } finally {
        setIsLoggingOut(false);
      }
    };
    const handleEscape = (event) => {
      if (event.key === "Escape") {
        setIsOpen(false);
        setIsThemePanelOpen(false);
        setIsInfoModalOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("touchstart", handleClickOutside);
    document.addEventListener("keydown", handleEscape);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("touchstart", handleClickOutside);
      document.removeEventListener("keydown", handleEscape);
    };
  }, []);

  const handleToggleMenu = (event) => {
    event.stopPropagation();

    setIsOpen((prev) => {
      const next = !prev;

      if (next) {
        checkStaffManagementAccess();
      } else {
        setIsThemePanelOpen(false);
      }

      return next;
    });
  };

  const closeMenu = () => {
    setIsOpen(false);
    setIsThemePanelOpen(false);
  };

  const handleOpenChangePasswordModal = () => {
    closeMenu();
    setIsChangePasswordModalOpen(true);
  };

  const handleOpenInfoModal = () => {
    closeMenu();
    setIsInfoModalOpen(true);
  };
  const handleOpenDevicesModal = () => {
    closeMenu();
    setIsDevicesModalOpen(true);
  };

  const handleOpenStaffManagementModal = () => {
    closeMenu();
    setIsStaffManagementModalOpen(true);
  };

  const handleToggleThemePanel = (event) => {
    event.stopPropagation();
    setIsThemePanelOpen((prev) => !prev);
  };

  const handleSelectTheme = (nextThemeKey) => {
    setTheme(nextThemeKey);
    closeMenu();
  };

  const handleOpenLogoutConfirm = () => {
    closeMenu();
    setIsLogoutConfirmOpen(true);
  };

  const handleConfirmLogout = async () => {
    try {
      setIsLoggingOut(true);

      if (onLogout) {
        const result = onLogout();

        if (result && typeof result.then === "function") {
          await result;
        }

        setIsLogoutConfirmOpen(false);
        return;
      }

      localStorage.removeItem("token");
      localStorage.removeItem("user");
      localStorage.removeItem("currentUser");
      localStorage.removeItem("device");
      localStorage.removeItem("selectedDevice");

      setIsLogoutConfirmOpen(false);
      navigate(routes.login || "/login");
    } catch (error) {
      console.error("خطا در خروج از حساب:", error);
    } finally {
      setIsLoggingOut(false);
    }
  };

  const getDisplayUserName = () => {
    if (userName) return userName;

    if (user) {
      return (
        user.username ||
        user.userName ||
        user.user_name ||
        user.firstName ||
        user.first_name ||
        user.name ||
        "کاربر"
      );
    }

    try {
      const storedUser =
        localStorage.getItem("user") || localStorage.getItem("currentUser");

      if (!storedUser) return "کاربر";

      const parsedUser = JSON.parse(storedUser);

      return (
        parsedUser.username ||
        parsedUser.userName ||
        parsedUser.user_name ||
        parsedUser.firstName ||
        parsedUser.first_name ||
        parsedUser.name ||
        "کاربر"
      );
    } catch (error) {
      console.error("خطا در خواندن نام کاربر:", error);
      return "کاربر";
    }
  };

  const displayUserName = getDisplayUserName();

  return (
    <>
      <div
        ref={menuRef}
        className={`
        ${styles.userMenu}
        ${isOpen ? styles.userMenuOpen : ""}
    `}
        data-hint="ناحیه کاربری"
      >
        <button
          type="button"
          className={`
        ${styles.userButton}
        ${isOpen ? styles.userButtonActive : ""}
    `}
          onClick={handleToggleMenu}
          aria-label="ناحیه کاربری"
          aria-expanded={isOpen}
        >
          <FaUserCircle />
        </button>

        {isOpen && (
          <div
            className={`
                            ${styles.dropdown}
                            ${dropdownAlign === "left" ? styles.dropdownLeft : styles.dropdownRight}
                        `}
          >
            <div className={styles.dropdownHeader}>
              <FaUserCircle className={styles.headerIcon} />

              <div className={styles.headerText}>
                <span className={styles.headerTitle}>ناحیه کاربری</span>

                <span className={styles.headerSubtitle} title={displayUserName}>
                  {displayUserName}
                </span>
              </div>
            </div>

            <button
              type="button"
              className={styles.menuItem}
              onClick={handleOpenInfoModal}
            >
              <FaInfoCircle />
              <span>اطلاعات</span>
            </button>

            <button
              type="button"
              className={styles.menuItem}
              onClick={handleOpenDevicesModal}
            >
              <FaMicrochip />
              <span>دستگاه‌های من</span>
            </button>

            <button
              type="button"
              className={styles.menuItem}
              onClick={handleOpenChangePasswordModal}
            >
              <FaKey />
              <span>تغییر رمز عبور</span>
            </button>

            <button
              type="button"
              className={`${styles.menuItem} ${isThemePanelOpen ? styles.menuItemActive : ""}`}
              onClick={handleToggleThemePanel}
              aria-expanded={isThemePanelOpen}
            >
              <FaPalette />
              <span>تغییر تم</span>
            </button>

            {isThemePanelOpen && (
              <div
                className={styles.themePanel}
                onClick={(event) => event.stopPropagation()}
              >
                {themes.map((themeItem) => {
                  const isActiveTheme = themeItem.key === themeKey;

                  return (
                    <button
                      key={themeItem.key}
                      type="button"
                      className={`${styles.themeOption} ${isActiveTheme ? styles.themeOptionActive : ""}`}
                      onClick={() => handleSelectTheme(themeItem.key)}
                    >
                      <span
                        className={`${styles.themePreview} ${styles[themeItem.swatchClass] || ""}`}
                        aria-hidden="true"
                      ></span>

                      <span className={styles.themeTextBox}>
                        <span className={styles.themeTitleRow}>
                          <span className={styles.themeName}>
                            {themeItem.label}
                          </span>
                          {isActiveTheme && (
                            <FaCheck className={styles.themeCheckIcon} />
                          )}
                        </span>

                        <span className={styles.themeDescription}>
                          {themeItem.description}
                        </span>
                      </span>
                    </button>
                  );
                })}
              </div>
            )}

            {isCheckingStaffAccess && !canManageStaff && (
              <button type="button" className={styles.menuItem} disabled>
                <FaUserPlus />
                <span>در حال بررسی دسترسی...</span>
              </button>
            )}

            {canManageStaff && (
              <button
                type="button"
                className={styles.menuItem}
                onClick={handleOpenStaffManagementModal}
              >
                <FaUserPlus />
                <span>مدیریت کارمندان</span>
              </button>
            )}

            <div className={styles.divider}></div>

            <button
              type="button"
              className={`${styles.menuItem} ${styles.logoutItem}`}
              onClick={handleOpenLogoutConfirm}
            >
              <FaSignOutAlt />
              <span>خروج</span>
            </button>
          </div>
        )}
      </div>

      <UserInfoModal
        isOpen={isInfoModalOpen}
        onClose={() => setIsInfoModalOpen(false)}
        user={user}
        onSaveUserInfo={onSaveUserInfo}
      />
      <UserLogoutConfirmModal
        isOpen={isLogoutConfirmOpen}
        onClose={() => setIsLogoutConfirmOpen(false)}
        onConfirm={handleConfirmLogout}
        isLoading={isLoggingOut}
      />
      <UserDevicesModal
        isOpen={isDevicesModalOpen}
        onClose={() => setIsDevicesModalOpen(false)}
      />
      <UserChangePasswordModal
        isOpen={isChangePasswordModalOpen}
        onClose={() => setIsChangePasswordModalOpen(false)}
      />

      <StaffManagementModal
        isOpen={isStaffManagementModalOpen}
        onClose={() => setIsStaffManagementModalOpen(false)}
      />
    </>
  );
};

export default UserMenu;
